<script setup lang="ts">
import type { Props } from '@/widgets/productCard/model/types/product';
import type { IBadgeItems } from '@/shared/ui/Badge/types';

import FavoriteButton from '@/features/FavoriteButton';
import Badge from '@/shared/ui/Badge/index.vue';
import ProductCartButton from '@/features/ProductCartButton/index.vue';
import { formatSum } from '@/shared/utils/formatSum';
import { PRODUCT_IMAGE_PLACEHOLDER_PATH } from '@/shared/constants/placeholders';
import { generateProductDetailLink } from '@/shared/utils/generateProductDetailLink';
import { MIN_BOUGHT_AMOUNT } from '@/shared/constants/badges';
import { useCategoriesStore } from '@/entities/categories/index';

const { product } = defineProps<Props>();
const isHovered: Ref<boolean> = ref(false);
const isFavorite: Ref<boolean> = ref(product?.is_favorite || false);

const categoriesStore = useCategoriesStore();
const { locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();

const badges: Ref<IBadgeItems[]> = ref([
  {
    id: 1,
    text: 'Ликвидация',
    type: 'liquidation',
    isShow: Boolean(product.liquidation),
  },
  {
    id: 2,
    text: String(product.bought_count),
    type: 'bought',
    isShow: Boolean(product.bought_count > MIN_BOUGHT_AMOUNT),
  },
  {
    id: 3,
    text: String(product.in_stock),
    type: 'remained',
    isShow: Boolean(product.in_stock),
  },
  {
    id: 4,
    text: 'Новинка',
    type: 'new_arrival',
    isShow: Boolean(product.new_arrival),
  },
]);

const hasImages = computed(() => {
  return product?.images && product?.images?.length > 0;
});

const currentImageUrl = computed(() => {
  if (!hasImages.value) {
    return '';
  }

  if (product?.images?.length === 1) {
    return product?.images[0]?.image;
  }

  return isHovered.value ? product?.images[1]?.image : product?.images[0]?.image;
});

const cardLink = computed(() =>
  generateProductDetailLink(
    product,
    categoriesStore.categories,
    locale.value,
    localePath,
  ),
);
</script>

<template>
  <article
    :class="[
      'product-card',
      { _sale: product?.price_with_discount },
      { _hovered: isHovered },
    ]"
    itemscope
    itemtype="https://schema.org/Product"
    :itemid="'#product-' + product?.id"
  >
    <meta itemprop="name" :content="product?.name" />
    <meta itemprop="description" :content="product?.description" />
    <meta itemprop="brand" :content="product?.brand?.name" />
    <meta itemprop="sku" :content="product?.article" />
    <meta itemprop="mpn" :content="product?.article" />
    <meta itemprop="category" :content="product?.category?.name" />

    <div class="product-card__favorite">
      <FavoriteButton v-model="isFavorite" :entity-id="product?.id" />
    </div>
    <nuxt-link class="product-card__link" :to="cardLink" :aria-label="product?.name">
      <div class="product-card__header">
        <NuxtImg
          class="product-card__image"
          :src="currentImageUrl"
          :alt="product?.name"
          itemprop="image"
          loading="lazy"
          :placeholder="PRODUCT_IMAGE_PLACEHOLDER_PATH"
          @mouseenter="isHovered = true"
          @mouseleave="isHovered = false"
        />
        <ul class="product-card__badges">
          <li
            v-for="badge in badges"
            :key="badge.id"
            v-show="badge.isShow"
            class="product-card__badge"
          >
            <Badge :id="badge.id" :text="badge?.text" :type="badge?.type" />
          </li>
        </ul>
      </div>
      <div class="product-card__body">
        <div class="product-card__content-info">
          <div class="product-card__name">
            {{ product?.name }}
          </div>
          <div
            class="product-card__price-wrapper"
            itemprop="offers"
            itemscope
            itemtype="https://schema.org/Offer"
          >
            <meta itemprop="price" :content="product.price.toString()" />
            <meta itemprop="priceCurrency" content="UZS" />
            <meta itemprop="availability" content="https://schema.org/InStock" />

            <span
              v-if="product?.price_with_discount !== product?.price"
              class="product-card__price _new"
            >
              {{ formatSum(product?.price_with_discount) }} UZS
            </span>
            <span
              v-if="product?.price"
              :class="[
                'product-card__price',
                { _old: product?.price_with_discount !== product?.price },
              ]"
            >
              {{ formatSum(product?.price) }} UZS
            </span>
          </div>
        </div>
      </div>
    </nuxt-link>
    <div class="product-card__button-wrapper" v-if="product?.id">
      <ProductCartButton :product="product" />
    </div>
  </article>
</template>

<style lang="less">
.product-card {
  .flex-column(@size-x6);

  position: relative;
  overflow: hidden;
  scroll-snap-align: center;
  height: 428px;
  width: 100%;
  border-radius: @size-x4;
  box-shadow: 0px 5px 9px 0px rgba(#000, 0.04);

  &__link {
    .flex-column(@size-x4);
  }

  &__header {
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    height: 232px;
    background-color: @color-alto-secondary;
  }

  &__image {
    display: block;
    object-fit: cover;
    max-width: 100%;
    width: 100%;
    height: 100%;
    transition: transform @A ease-in-out;

    &._empty {
      width: 135px;
      height: 125px;
      margin: 0 auto;
    }
  }

  &._hovered &__image {
    transform: scale(1.2);
  }

  &__favorite {
    position: absolute;
    top: @size-x3;
    right: @size-x3;
    z-index: 1;
  }

  &__badges {
    .flex-column(@main-size);

    position: absolute;
    left: @size-x3;
    bottom: @size-x3;
  }

  &__body {
    .flex-column(@size-x6);

    padding: 0 @size-x3;
  }

  &__content-info {
    .flex-column(@size-x3);
  }

  &__name {
    .overflow-ellipsis-lines(3);

    font-size: @size-x4;
    line-height: 130%;
    font-weight: 500;
    color: @color-mine-shaft;
  }

  &__price-wrapper {
    display: inline-flex;
    align-items: center;
    gap: @size-x3;
  }

  &__price {
    display: inline-flex;
    font-size: 17px;
    line-height: 130%;
    font-weight: 700;
    color: @color-mine-shaft;
  }

  &._sale &__price._old {
    font-size: 15px;
    color: @color-silver-chalice;
    text-decoration: line-through;
    text-decoration-thickness: 2px;
  }

  &._sale &__price._new {
    color: @color-carnation;
  }

  &__button-wrapper {
    margin-top: auto;
    padding: 0 @size-x3 @size-x4;
  }

  @media @1024 {
    gap: @size-x3;
    height: 249px;

    &__link {
      gap: @size-x2;
    }

    &__header {
      height: 120px;
    }

    &__favorite {
      top: @size-x2;
      right: @size-x2;
    }

    &__image {
      &._empty {
        width: 80px;
        height: 80px;
      }
    }

    &__name {
      .overflow-ellipsis-lines(2);

      font-size: 13px;
    }

    &__price-wrapper {
      gap: @main-size;
    }

    &__price {
      font-size: 13px;
    }

    &._sale &__price._old {
      font-size: 11px;
      text-decoration-thickness: 1px;
    }

    &__content-info {
      gap: 6px;
    }

    &__badges {
      gap: 2px;
      bottom: 6px;
      left: 6px;
    }

    &__button-wrapper {
      padding: 0 @size-x3 @size-x4;
    }
  }
}
</style>
